import Api from '../api';
import { API_CONFIG } from '../config';

/**
 * Leave API Service
 * Handles all leave-related API calls
 */

const leaveService = {
  /**
   * Get leave enrollments for an employee
   * @param {string} personnelNumber - Employee personnel number
   * @returns {Promise} Axios response with leave enrollments data
   */
  async getLeaveEnrollments(personnelNumber) {
    console.log('📋 Fetching leave enrollments for:', personnelNumber);

    const queryParams = {
      'cross-company': true,
      $filter: `PersonnelNumber eq '${personnelNumber}'`,
    };

    try {
      const response = await Api.get('/data/LeaveEnrollmentsV2', { params: queryParams });
      console.log('✅ Leave enrollments fetched:', response.data?.value?.length || 0, 'records');
      return response;
    } catch (error) {
      console.error('❌ Error fetching leave enrollments:', error);
      throw error;
    }
  },

  /**
   * Get leave plan tiers
   * @returns {Promise} Axios response with leave plan tiers data
   */
  async getLeavePlanTiers() {
    console.log('📋 Fetching leave plan tiers');

    const queryParams = {
      'cross-company': true,
    };

    try {
      const response = await Api.get('/data/LeavePlanTiersV2', { params: queryParams });
      console.log('✅ Leave plan tiers fetched:', response.data?.value?.length || 0, 'records');
      return response;
    } catch (error) {
      console.error('❌ Error fetching leave plan tiers:', error);
      throw error;
    }
  },

  /**
   * Get all leave types
   * @param {Object} params - Query parameters
   * @returns {Promise} Axios response with leave types data
   */
  async getLeaveTypes(params = {}) {
    console.log('📋 Fetching leave types from API');

    const queryParams = {
      'cross-company': true,
      ...params,
    };

    try {
      const response = await Api.get('/data/LeaveTypes', { params: queryParams });
      console.log('✅ Leave types fetched:', response.data?.value?.length || 0, 'types');
      return response;
    } catch (error) {
      console.error('❌ Error fetching leave types:', error);
      throw error;
    }
  },

  /**
   * Get my leave requests (current user)
   * @param {Object} params - Query parameters
   * @returns {Promise} Axios response with leave request data
   */
  async getMyLeaveRequests(params = {}) {
    const queryParams = {
      'cross-company': API_CONFIG.DEFAULT_PARAMS.CROSS_COMPANY,
      ...params,
    };

    return Api.get('/data/MyLeaveRequests', { params: queryParams });
  },

  /**
   * Get ESS leave request headers
   * @param {Object} params - Query parameters
   * @param {string} params.$filter - OData filter query
   * @param {string} params.$select - OData select fields
   * @param {string} params.$orderby - OData orderby clause
   * @returns {Promise} Axios response with leave request headers
   */
  async getEssLeaveRequestHeaders(params = {}) {
    const queryParams = {
      'cross-company': API_CONFIG.DEFAULT_PARAMS.CROSS_COMPANY,
      ...params,
    };

    return Api.get('/data/EssLeaveRequestHeaders', { params: queryParams });
  },

  /**
   * Get a single ESS leave request header by RequestId and dataAreaId
   * @param {string} requestId - Request ID (e.g., 'SHP-1777444884133')
   * @param {string} dataAreaId - Data Area ID (e.g., 'shp')
   * @returns {Promise} Axios response with leave request header
   */
  async getEssLeaveRequestHeaderById(requestId, dataAreaId) {
    const params = {
      'cross-company': true,
    };

    return Api.get(`/data/EssLeaveRequestHeaders(RequestId='${requestId}',dataAreaId='${dataAreaId}')`, { params });
  },

  /**
   * Get leave request headers
   * @param {Object} params - Query parameters
   * @returns {Promise} Axios response with leave request headers
   */
  async getLeaveRequestHeaders(params = {}) {
    const queryParams = {
      'cross-company': API_CONFIG.DEFAULT_PARAMS.CROSS_COMPANY,
      ...params,
    };

    return Api.get('/data/LeaveRequestHeaders', { params: queryParams });
  },

  /**
   * Create a new leave request header
   * @param {Object} data - Leave request header data
   * @param {string} data.dataAreaId - Company ID
   * @param {string} data.ReasonCodeId - Reason code
   * @param {string} data.PersonnelNumber - Employee personnel number
   * @param {string} data.RequestDate - Request date (ISO format)
   * @param {string} data.Comment - Request comment
   * @returns {Promise} Axios response with created leave request
   */
  async createLeaveRequestHeader(data) {
    const params = {
      'cross-company': API_CONFIG.DEFAULT_PARAMS.CROSS_COMPANY,
    };

    return Api.post('/data/LeaveRequestHeaders', data, { params });
  },

  /**
   * Get leave requests V2
   * @param {Object} params - Query parameters
   * @param {string} params.$filter - OData filter query
   * @param {string} params.$select - OData select fields
   * @param {string} params.$orderby - OData orderby clause
   * @returns {Promise} Axios response with leave requests
   */
  async getLeaveRequestsV2(params = {}) {
    const queryParams = {
      'cross-company': API_CONFIG.DEFAULT_PARAMS.CROSS_COMPANY,
      ...params,
    };

    return Api.get('/data/LeaveRequestsV2', { params: queryParams });
  },

  /**
   * Helper function to format date as YYYY-MM-DD in local timezone (no UTC conversion)
   */
  formatDateLocal(date) {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  },

  /**
   * Create a new leave request (V2)
   * Creates one request per day in the date range
   * @param {Object} data - Leave request data
   * @param {string} data.personnelNumber - Employee personnel number
   * @param {string} data.leaveTypeId - Leave type code (Bereavement, PTO, Sick, Vacation)
   * @param {string} data.startDate - Start date (YYYY-MM-DD)
   * @param {string} data.endDate - End date (YYYY-MM-DD)
   * @param {string} data.reason - Request comment/reason
   * @param {number} data.hoursPerDay - Hours per day (default 8)
   * @param {string} data.halfDayDefinition - Half day definition (None, FirstHalf, SecondHalf)
   * @returns {Promise} Array of created leave request responses
   */
  async createLeaveRequest(data) {
    console.log('📝 Creating leave request:', data);

    const {
      personnelNumber,
      leaveTypeId,
      startDate,
      endDate,
      reason,
      hoursPerDay = 8,
      halfDayDefinition = 'None',
      dataAreaId = 'usmf'
    } = data;

    // Generate unique request ID
    const timestamp = Date.now();
    const requestId = `${dataAreaId.toUpperCase()}-${timestamp}`;

    // Calculate all dates in the range
    const dates = [];
    const start = new Date(startDate);
    const end = new Date(endDate);

    for (let dt = new Date(start); dt <= end; dt.setDate(dt.getDate() + 1)) {
      dates.push(new Date(dt));
    }

    console.log(`📅 Creating ${dates.length} leave request records (one per day)`);

    // Create one API call per day
    const params = {
      'cross-company': API_CONFIG.DEFAULT_PARAMS.CROSS_COMPANY,
    };

    const requests = [];

    for (let i = 0; i < dates.length; i++) {
      const leaveDate = dates[i];

      // Format date as ISO string (YYYY-MM-DDTHH:mm:ssZ) - using local date to avoid timezone shift
      const leaveDateStr = this.formatDateLocal(leaveDate) + 'T12:00:00Z';
      const requestDateStr = new Date().toISOString();

      const requestPayload = {
        dataAreaId: dataAreaId,
        RequestId: requestId,
        LeaveType: leaveTypeId,
        LeaveDate: leaveDateStr,
        ReasonCodeId: '',
        PersonnelNumber: personnelNumber,
        RequestDate: requestDateStr,
        Comment: reason,
        Amount: hoursPerDay,
        HalfDayDefinition: halfDayDefinition,
      };

      console.log(`📤 Creating leave request ${i + 1}/${dates.length}:`, requestPayload);

      try {
        const response = await Api.post('/data/LeaveRequestsV2', requestPayload, { params });
        requests.push(response);
        console.log(`✅ Leave request ${i + 1}/${dates.length} created successfully`);
      } catch (error) {
        console.error(`❌ Failed to create leave request ${i + 1}/${dates.length}:`, error.response?.data);
        throw new Error(
          `Failed to create leave request for ${leaveDateStr}: ${
            error.response?.data?.error?.message || error.message
          }`
        );
      }
    }

    console.log(`✅ All ${dates.length} leave request records created successfully`);
    return requests;
  },

  /**
   * Create leave request with individual day amounts
   * @param {Object} data - Leave request data
   * @param {string} data.personnelNumber - Employee personnel number
   * @param {string} data.leaveTypeId - Leave type code
   * @param {string} data.reason - Request comment/reason
   * @param {string} data.halfDayDefinition - Half day definition (None, FirstHalf, SecondHalf)
   * @param {string} data.dataAreaId - Data area ID
   * @param {Array} data.leaveDays - Array of { date, amount } objects
   * @returns {Promise} Array of created leave request responses
   */
  async createLeaveRequestWithDays(data) {
    console.log('📝 Creating leave request with individual days:', data);

    const {
      personnelNumber,
      leaveTypeId,
      reason,
      halfDayDefinition = 'None',
      dataAreaId = 'usmf',
      leaveDays = [],
    } = data;

    if (leaveDays.length === 0) {
      throw new Error('No leave days provided');
    }

    // Generate unique request ID
    const timestamp = Date.now();
    const requestId = `${dataAreaId.toUpperCase()}-${timestamp}`;

    console.log(`📅 Creating ${leaveDays.length} leave request records`);

    const params = {
      'cross-company': API_CONFIG.DEFAULT_PARAMS.CROSS_COMPANY,
    };

    const requests = [];

    for (let i = 0; i < leaveDays.length; i++) {
      const leaveDay = leaveDays[i];

      // Format date as ISO string (YYYY-MM-DDTHH:mm:ssZ)
      const leaveDateStr = leaveDay.date + 'T12:00:00Z';
      const requestDateStr = new Date().toISOString();

      const requestPayload = {
        dataAreaId: dataAreaId,
        RequestId: requestId,
        LeaveType: leaveTypeId,
        LeaveDate: leaveDateStr,
        ReasonCodeId: '',
        PersonnelNumber: personnelNumber,
        RequestDate: requestDateStr,
        Comment: reason,
        Amount: leaveDay.amount, // 1 for full day, 0.5 for half day
        HalfDayDefinition: halfDayDefinition,
      };

      console.log(`📤 Creating leave request ${i + 1}/${leaveDays.length}:`, requestPayload);

      try {
        const response = await Api.post('/data/LeaveRequestsV2', requestPayload, { params });
        requests.push(response);
        console.log(`✅ Leave request ${i + 1}/${leaveDays.length} created successfully`);
      } catch (error) {
        console.error(`❌ Failed to create leave request ${i + 1}/${leaveDays.length}:`, error.response?.data);
        throw new Error(
          `Failed to create leave request for ${leaveDay.date}: ${
            error.response?.data?.error?.message || error.message
          }`
        );
      }
    }

    console.log(`✅ All ${leaveDays.length} leave request records created successfully`);
    return requests;
  },

  /**
   * Update leave request
   * @param {string} requestId - Request ID
   * @param {Object} data - Updated leave request data
   * @returns {Promise} Axios response with updated leave request
   */
  async updateLeaveRequest(requestId, data) {
    const params = {
      'cross-company': API_CONFIG.DEFAULT_PARAMS.CROSS_COMPANY,
    };

    return Api.patch(`/data/LeaveRequestsV2?$filter=RequestId eq '${requestId}'`, data, { params });
  },

  /**
   * Cancel leave request (by updating LeaveStatus to Cancelled)
   * @param {string} requestId - Request ID (e.g., 'USMF-000074')
   * @param {string} dataAreaId - Data Area ID (e.g., 'usmf')
   * @returns {Promise} Axios response
   */
  async cancelLeaveRequest(requestId, dataAreaId = 'usmf') {
    console.log(`🗑️ Cancelling leave request: ${requestId}, dataAreaId: ${dataAreaId}`);

    const params = {
      'cross-company': true,
    };

    const headers = {
      'Content-Type': 'application/json',
      'If-Match': '*',
    };

    const url = `/data/EssLeaveRequestHeaders(RequestId='${requestId}',dataAreaId='${dataAreaId}')`;
    console.log('📤 PATCH:', url);

    try {
      const response = await Api.patch(
        url,
        { LeaveStatus: 'Cancelled' },
        { params, headers }
      );
      console.log('✅ Leave request cancelled successfully');
      return response;
    } catch (error) {
      console.error('❌ Cancel failed:', error.response?.status, error.response?.data?.error?.message);
      throw new Error(
        `Failed to cancel leave request: ${error.response?.data?.error?.message || error.message}`
      );
    }
  },

  /**
   * Delete leave request (deprecated - use cancelLeaveRequest instead)
   * @param {string} requestId - Request ID
   * @returns {Promise} Axios response
   */
  async deleteLeaveRequest(requestId) {
    return this.cancelLeaveRequest(requestId);
  },

  /**
   * Update the LeaveStatus of a leave request header (approve / reject / etc.)
   * PATCHes EssLeaveRequestHeaders(RequestId=...,dataAreaId=...).
   * @param {string} requestId - Request ID (e.g., 'USMF-000077')
   * @param {string} dataAreaId - Data Area ID (e.g., 'usmf')
   * @param {string} leaveStatus - New status ('Approved', 'Rejected', 'Cancelled', ...)
   * @returns {Promise} Axios response
   */
  async updateLeaveRequestStatus(requestId, dataAreaId = 'usmf', leaveStatus) {
    console.log(`📝 Updating leave request ${requestId} → ${leaveStatus}`);

    const params = { 'cross-company': true };
    const headers = {
      'Content-Type': 'application/json',
      'If-Match': '*',
    };

    const url = `/data/EssLeaveRequestHeaders(RequestId='${requestId}',dataAreaId='${dataAreaId}')`;

    try {
      const response = await Api.patch(url, { LeaveStatus: leaveStatus }, { params, headers });
      console.log(`✅ Leave request ${requestId} updated to ${leaveStatus}`);
      return response;
    } catch (error) {
      console.error(
        `❌ Failed to update leave request ${requestId}:`,
        error.response?.status,
        error.response?.data?.error?.message
      );
      throw new Error(
        `Failed to update leave request: ${error.response?.data?.error?.message || error.message}`
      );
    }
  },

  /**
   * Approve a leave request
   * @param {string} requestId - Request ID
   * @param {string} dataAreaId - Data Area ID
   * @returns {Promise} Axios response
   */
  async approveLeaveRequest(requestId, dataAreaId = 'usmf') {
    return this.updateLeaveRequestStatus(requestId, dataAreaId, 'Approved');
  },

  /**
   * Reject a leave request
   * @param {string} requestId - Request ID
   * @param {string} dataAreaId - Data Area ID
   * @returns {Promise} Axios response
   */
  async rejectLeaveRequest(requestId, dataAreaId = 'usmf') {
    return this.updateLeaveRequestStatus(requestId, dataAreaId, 'Rejected');
  },

  /**
   * Get ESS leave balances
   * @param {Object} params - Query parameters
   * @param {string} params.$filter - OData filter query (e.g., filter by employee)
   * @returns {Promise} Axios response with leave balances
   */
  async getEssLeaveBalances(params = {}) {
    const queryParams = {
      'cross-company': API_CONFIG.DEFAULT_PARAMS.CROSS_COMPANY,
      ...params,
    };

    return Api.get('/data/EssLeaveBalances', { params: queryParams });
  },

  /**
   * Get leave balances
   * @param {Object} params - Query parameters
   * @param {string} params.$filter - OData filter query
   * @returns {Promise} Axios response with leave balances
   */
  async getLeaveBalances(params = {}) {
    const queryParams = {
      'cross-company': API_CONFIG.DEFAULT_PARAMS.CROSS_COMPANY,
      ...params,
    };

    return Api.get('/data/LeaveBalances', { params: queryParams });
  },

  /**
   * Get leave balances by employee
   * @param {string} personnelNumber - Employee personnel number
   * @returns {Promise} Axios response with leave balances
   */
  async getLeaveBalancesByEmployee(personnelNumber) {
    const params = {
      $filter: `PersonnelNumber eq '${personnelNumber}'`,
      'cross-company': API_CONFIG.DEFAULT_PARAMS.CROSS_COMPANY,
    };

    return Api.get('/data/EssLeaveBalances', { params });
  },

  /**
   * Get leave requests by employee
   * @param {string} personnelNumber - Employee personnel number
   * @param {Object} additionalParams - Additional query parameters
   * @returns {Promise} Axios response with leave requests
   */
  async getLeaveRequestsByEmployee(personnelNumber, additionalParams = {}) {
    // Base filter for personnel number
    let filter = `PersonnelNumber eq '${personnelNumber}'`;

    // If additional params has $filter, combine with AND
    if (additionalParams.$filter) {
      filter = `${filter} and ${additionalParams.$filter}`;
      // Remove $filter from additionalParams to avoid duplication
      const { $filter, ...restParams } = additionalParams;
      additionalParams = restParams;
    }

    const params = {
      $filter: filter,
      'cross-company': API_CONFIG.DEFAULT_PARAMS.CROSS_COMPANY,
      ...additionalParams,
    };

    return Api.get('/data/LeaveRequestsV2', { params });
  },

  /**
   * Get an employee's LeaveRequestsV2 lines that fall on a single day.
   *
   * Used by the Manager Dashboard "On leave today" metric. Filtering on
   * LeaveDate keeps the query narrow — we only pull the day's lines rather than
   * an employee's whole leave history.
   *
   * @param {string} personnelNumber - Employee personnel number
   * @param {string} fromIso - Start-of-day ISO bound (e.g. '2026-07-22T00:00:00Z')
   * @param {string} toIso - End-of-day ISO bound (e.g. '2026-07-22T23:59:59Z')
   * @returns {Promise} Axios response with leave request day-lines
   */
  async getLeaveRequestsForEmployeeOnDay(personnelNumber, fromIso, toIso) {
    const params = {
      $filter: `PersonnelNumber eq '${personnelNumber}' and LeaveDate ge ${fromIso} and LeaveDate le ${toIso}`,
      'cross-company': API_CONFIG.DEFAULT_PARAMS.CROSS_COMPANY,
    };

    return Api.get('/data/LeaveRequestsV2', { params });
  },

  /**
   * Get an employee's LeaveRequestsV2 lines within a date window.
   *
   * Used by the Manager Dashboard "Staff on leave (in next 30 days)" metric.
   * Filtering on both PersonnelNumber and LeaveDate keeps the query scoped to
   * one employee's lines inside the window rather than their whole history.
   *
   * @param {string} personnelNumber - Employee personnel number
   * @param {string} fromIso - Inclusive lower LeaveDate bound (ISO date-time)
   * @param {string} toIso - Inclusive upper LeaveDate bound (ISO date-time)
   * @returns {Promise} Axios response with leave request day-lines
   */
  async getLeaveRequestsForEmployeeInRange(personnelNumber, fromIso, toIso) {
    const params = {
      $filter: `PersonnelNumber eq '${personnelNumber}' and LeaveDate ge ${fromIso} and LeaveDate le ${toIso}`,
      $orderby: 'LeaveDate asc',
      'cross-company': API_CONFIG.DEFAULT_PARAMS.CROSS_COMPANY,
    };

    return Api.get('/data/LeaveRequestsV2', { params });
  },

  /**
   * Get leave requests by date range
   * @param {string} startDate - Start date (ISO format)
   * @param {string} endDate - End date (ISO format)
   * @param {Object} additionalParams - Additional query parameters
   * @returns {Promise} Axios response with leave requests
   */
  async getLeaveRequestsByDateRange(startDate, endDate, additionalParams = {}) {
    const params = {
      $filter: `LeaveDate ge ${startDate} and LeaveDate le ${endDate}`,
      'cross-company': API_CONFIG.DEFAULT_PARAMS.CROSS_COMPANY,
      ...additionalParams,
    };

    return Api.get('/data/LeaveRequestsV2', { params });
  },

  /**
   * Get work calendar days for an employee
   * Returns calendar data with status: Open (working day), Closed (weekend), Holiday
   * @param {string} personnelNumber - Employee personnel number
   * @param {Object} additionalParams - Additional query parameters
   * @returns {Promise} Axios response with work calendar data
   */
  async getWorkCalendarDays(personnelNumber, additionalParams = {}) {
    console.log('📅 Fetching work calendar days for:', personnelNumber);

    // Base filter for personnel number
    let filter = `PersonnelNumber eq '${personnelNumber}'`;

    // If additional params has $filter, combine with AND
    if (additionalParams.$filter) {
      filter = `${filter} and ${additionalParams.$filter}`;
      const { $filter, ...restParams } = additionalParams;
      additionalParams = restParams;
    }

    const params = {
      $filter: filter,
      'cross-company': API_CONFIG.DEFAULT_PARAMS.CROSS_COMPANY,
      ...additionalParams,
    };

    try {
      const response = await Api.get('/data/EssWorkCalendarDays', { params });
      console.log('✅ Work calendar days fetched:', response.data?.value?.length || 0, 'records');
      return response;
    } catch (error) {
      console.error('❌ Error fetching work calendar days:', error);
      throw error;
    }
  },

  /**
   * Get leave bank transactions for an employee
   * @param {string} personnelNumber - Employee personnel number
   * @param {string} leaveTypeId - Leave type ID to filter by (optional)
   * @param {Object} additionalParams - Additional query parameters
   * @returns {Promise} Axios response with leave bank transactions
   */
  async getLeaveBankTransactions(personnelNumber, leaveTypeId = null, additionalParams = {}) {
    console.log('📊 Fetching leave bank transactions for:', personnelNumber, leaveTypeId ? `LeaveType: ${leaveTypeId}` : '');

    // Build filter
    let filter = `PersonnelNumber eq '${personnelNumber}'`;

    if (leaveTypeId) {
      filter = `${filter} and LeaveTypeId eq '${leaveTypeId}'`;
    }

    // If additional params has $filter, combine with AND
    if (additionalParams.$filter) {
      filter = `${filter} and ${additionalParams.$filter}`;
      const { $filter, ...restParams } = additionalParams;
      additionalParams = restParams;
    }

    const params = {
      $filter: filter,
      'cross-company': API_CONFIG.DEFAULT_PARAMS.CROSS_COMPANY,
      ...additionalParams,
    };

    try {
      const response = await Api.get('/data/LeaveBankTransactionsV2', { params });
      console.log('✅ Leave bank transactions fetched:', response.data?.value?.length || 0, 'records');
      return response;
    } catch (error) {
      console.error('❌ Error fetching leave bank transactions:', error);
      throw error;
    }
  },

  async getPendingLeaveRequests(filters = {}) {
    const params = {
      'cross-company': API_CONFIG.DEFAULT_PARAMS.CROSS_COMPANY,
      ...filters,
    };

    try {
      const response = await Api.get('/data/LeaveRequestHeaders', { params });
      const all = response.data?.value ?? [];
      const pending = filters.status && filters.status !== 'All'
        ? all.filter((r) => r.Status === filters.status)
        : all.filter((r) => r.Status === 'Pending');
      return pending;
    } catch (error) {
      console.error('❌ Error fetching pending leave requests:', error);
      throw error;
    }
  },
};

export default leaveService;
